import { Badge } from 'src/design/atoms/Badge';
import { Text } from 'src/design/atoms/Text';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { MRT_ColumnDef } from 'mantine-react-table';
import { IconTrash } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { JobUserDto } from 'src/api/FiretrolApiClient';
import { useRemoveTeamMember } from 'src/api/queries/jobs/useJobMutations';
import { useJobs } from 'src/hooks/useJobs';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { BadgeShortList } from 'src/design/molecules/BadgeShortList';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { TeamMemberSelector } from './components/TeamMemberSelector';
import {
  DIVISION_SOURCE,
  DivisionSourceBadge,
} from './components/DivisionSourceBadge';

const getColumns = (
  onRemoveClick: (teamMember: JobUserDto) => void,
): MRT_ColumnDef<JobUserDto>[] => {
  return [
    {
      accessorKey: 'user.fullName',
      header: 'Name',
      Cell: ({ row }) => {
        const user = row.original.user;
        return (
          <Group gap="xs">
            <Text>{`${user?.lastName}, ${user?.firstName}`}</Text>
            <DivisionSourceBadge source={row.original.source} />
          </Group>
        );
      },
    },
    {
      id: 'roles',
      header: 'Roles',
      Cell: ({ row }) => {
        const roles =
          row.original.user?.userOrganizationRole?.map((role) => {
            return <Badge key={role.id}>{role.organizationRole?.name}</Badge>;
          }) || [];
        return <BadgeShortList items={roles} />;
      },
    },
  ];
};

export const Team: React.FC = () => {
  const { selectedJob, team, refreshTeam, loadingTeam } = useJobs();
  const removeTeamMember = useRemoveTeamMember();

  const onRemoveClick = (teamMember: JobUserDto) => {
    if (!selectedJob || !teamMember.userId) return;
    removeTeamMember.mutate({
      jobId: selectedJob.id,
      userId: teamMember.userId,
    });
  };

  const columns = useMemo(() => getColumns(onRemoveClick), [onRemoveClick]);

  const tableState = useTableState({
    identifier: 'team',
    columns: columns,
    defaultSorting: [{ id: 'user.fullName', desc: false }],
  });

  const tableExports = useTableExports<JobUserDto>('project-team', (data) => {
    return data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['Name']: `${item.user?.lastName}, ${item.user?.firstName}`,
      ['Roles']:
        item.user?.userOrganizationRole
          ?.map((r) => r.organizationRole?.name)
          .join(' | ') || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      renderRowActions: ({ row }) => {
        const teamMember = row.original;
        if (teamMember.source === DIVISION_SOURCE) {
          return (
            <Group>
              <Tooltip label="Access comes from the user's division. Remove it by editing the user's division access.">
                <Box>
                  <BasicActionIcon
                    color="red"
                    variant="light"
                    disabled
                    aria-label="Remove Team Member"
                  >
                    <IconTrash size={20} />
                  </BasicActionIcon>
                </Box>
              </Tooltip>
            </Group>
          );
        }
        return (
          <Group>
            <DeleteActionIcon
              requiredConfirmation={true}
              confirmationTitle="Remove Team Member"
              confirmationText="Are you sure you want to remove this team member?"
              onDelete={() => onRemoveClick(teamMember)}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: team,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <TeamMemberSelector existingUsers={team} />
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: team.length,
    isLoading: loadingTeam,
    isFetching: false,
    isError: false,
    ...tableExports,
    onRefresh: refreshTeam,
  });

  if (!selectedJob) return null;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob.name} - ${selectedJob.jobNumber}`,
              href: `/jobs/${selectedJob.id}/summary`,
            },
            { text: 'Project Team', href: '' },
          ],
        }}
        title="Project Team"
        helpText="Manage team members for this job."
      />
      {table}
    </>
  );
};
